import { find, findAll } from "utils/dom"

export default function initHmSelect(root = document) {
	const wraps = findAll("[data-hm-select]", root)
	if (!wraps?.length) return

	wraps.forEach((wrap) => {
		const native = find("select.hm-select__native", wrap)
		const trigger = find(".hm-select__trigger", wrap)
		const valueEl = find(".hm-select__value", wrap)
		const list = find(".hm-select__list", wrap)
		if (!native || !trigger || !valueEl || !list) return

		const placeholder =
			native.querySelector("option[value='']")?.textContent?.trim() || "Selecione..."

		const options = [...native.options].filter((opt) => !opt.disabled && opt.value !== "")

		list.innerHTML = ""
		options.forEach((opt) => {
			const li = document.createElement("li")
			li.setAttribute("role", "presentation")

			const btn = document.createElement("button")
			btn.type = "button"
			btn.className = "hm-select__option"
			btn.setAttribute("role", "option")
			btn.setAttribute("data-value", opt.value)
			btn.textContent = opt.textContent
			btn.setAttribute("aria-selected", "false")

			li.appendChild(btn)
			list.appendChild(li)
		})

		const optionBtns = () => [...list.querySelectorAll(".hm-select__option")]

		function syncFromNative() {
			const selected = native.options[native.selectedIndex]
			const hasValue = selected && selected.value

			valueEl.textContent = hasValue ? selected.textContent : placeholder
			valueEl.classList.toggle("hm-select__value--placeholder", !hasValue)

			optionBtns().forEach((btn) => {
				const isSelected = btn.dataset.value === native.value
				btn.classList.toggle("is-selected", isSelected)
				btn.setAttribute("aria-selected", String(isSelected))
			})
		}

		function positionList() {
			const rect = trigger.getBoundingClientRect()
			const gap = 8
			const vw = window.innerWidth
			const vh = window.innerHeight

			list.hidden = false
			list.style.visibility = "hidden"
			list.style.left = "0"
			list.style.top = "0"
			list.style.width = `${rect.width}px`
			list.style.maxWidth = `${Math.min(rect.width, vw - 16)}px`

			const listH = list.offsetHeight
			const spaceBelow = vh - rect.bottom - gap
			const spaceAbove = rect.top - gap
			const openUp = spaceBelow < listH && spaceAbove > spaceBelow

			let left = rect.left
			const width = Math.min(rect.width, vw - 16)
			if (left + width > vw - 8) left = vw - 8 - width
			if (left < 8) left = 8

			list.style.left = `${left}px`
			list.style.width = `${width}px`

			if (openUp) {
				const top = Math.max(8, rect.top - gap - listH)
				list.style.top = `${top}px`
				list.style.maxHeight = `${Math.min(280, rect.top - gap - 8)}px`
			} else {
				list.style.top = `${rect.bottom + gap}px`
				list.style.maxHeight = `${Math.min(280, vh - rect.bottom - gap - 8)}px`
			}

			list.style.visibility = ""
		}

		function close() {
			if (!wrap.classList.contains("is-open")) return
			wrap.classList.remove("is-open")
			trigger.setAttribute("aria-expanded", "false")
			list.hidden = true
			if (list.parentElement !== wrap) wrap.appendChild(list)
			window.removeEventListener("resize", onReposition)
			window.removeEventListener("scroll", onReposition, true)
		}

		function open() {
			wrap.classList.add("is-open")
			trigger.setAttribute("aria-expanded", "true")
			document.body.appendChild(list)
			positionList()
			window.addEventListener("resize", onReposition)
			window.addEventListener("scroll", onReposition, true)

			const selected = optionBtns().find((btn) => btn.classList.contains("is-selected"))
			;(selected || optionBtns()[0])?.focus()
		}

		function onReposition() {
			if (wrap.classList.contains("is-open")) positionList()
		}

		function toggle() {
			if (wrap.classList.contains("is-open")) close()
			else open()
		}

		trigger.addEventListener("click", (e) => {
			e.preventDefault()
			toggle()
		})

		list.addEventListener("click", (e) => {
			const btn = e.target.closest(".hm-select__option")
			if (!btn) return
			native.value = btn.dataset.value
			native.dispatchEvent(new Event("change", { bubbles: true }))
			syncFromNative()
			close()
			trigger.focus()
		})

		trigger.addEventListener("keydown", (e) => {
			if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
				e.preventDefault()
				if (!wrap.classList.contains("is-open")) open()
			} else if (e.key === "Escape") {
				close()
			}
		})

		list.addEventListener("keydown", (e) => {
			const items = optionBtns()
			const idx = items.indexOf(document.activeElement)

			if (e.key === "Escape") {
				e.preventDefault()
				close()
				trigger.focus()
				return
			}

			if (e.key === "ArrowDown") {
				e.preventDefault()
				items[Math.min(idx + 1, items.length - 1)]?.focus()
			} else if (e.key === "ArrowUp") {
				e.preventDefault()
				items[Math.max(idx - 1, 0)]?.focus()
			} else if (e.key === "Home") {
				e.preventDefault()
				items[0]?.focus()
			} else if (e.key === "End") {
				e.preventDefault()
				items[items.length - 1]?.focus()
			} else if (e.key === "Enter" || e.key === " ") {
				e.preventDefault()
				document.activeElement?.click()
			}
		})

		document.addEventListener("click", (e) => {
			if (!wrap.contains(e.target) && !list.contains(e.target)) close()
		})

		native.addEventListener("invalid", () => {
			trigger.focus()
			wrap.classList.add("is-invalid")
		})

		native.addEventListener("change", () => {
			wrap.classList.remove("is-invalid")
		})

		const form = native.closest("form")
		form?.addEventListener("reset", () => {
			requestAnimationFrame(() => {
				syncFromNative()
				close()
				wrap.classList.remove("is-invalid")
			})
		})

		syncFromNative()
	})
}
